import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useTicket,
  useSendTicketWhatsApp,
  useCancelTicket,
  useReleaseTicket,
  useRevokeDeliveryLink,
  useRegenerateDeliveryLink,
  canEditTicket,
} from '../hooks/useTickets';
import { useAuth } from '../hooks/useAuth';
import { isAdminOrSupport, isAdmin, isCustomerSupport, normalizeStatus } from '../utils/roles';
import { useMarkTicketNotificationsRead } from '../hooks/useNotifications';
import EditTicketModal from '../components/EditTicketModal';
import { userDisplayName, formatSlot } from '../utils/ticketDisplay';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import TicketStatusBadge from '../components/TicketStatusBadge';
import { ArrowLeft, Send, Ban, Link2, Video, Pencil, RefreshCw, ShieldOff } from 'lucide-react';
import { formatPhoneHint } from '../utils/phoneE164';

const TicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { data, isLoading } = useTicket(id || '');
  const sendMutation = useSendTicketWhatsApp();
  const cancelMutation = useCancelTicket();
  const releaseMutation = useReleaseTicket();
  const revokeLinkMutation = useRevokeDeliveryLink();
  const regenerateLinkMutation = useRegenerateDeliveryLink();
  const markTicketNotificationsRead = useMarkTicketNotificationsRead();

  const [showEditModal, setShowEditModal] = useState(false);
  const [phone, setPhone] = useState('');
  const [language, setLanguage] = useState('en');
  const [roomName, setRoomName] = useState('');

  const ticket = data?.ticket;
  const events = data?.events ?? [];
  const shortUrl = data?.shortUrl;
  const deliveryLinks = data?.deliveryLinks ?? [];
  const deliveryLogs = data?.deliveryLogs ?? [];
  const hasActiveLink = deliveryLinks.some((l) => l.isActive) || !!shortUrl;

  React.useEffect(() => {
    if (ticket) {
      setPhone(ticket.customerPhone || '');
      setLanguage(ticket.preferredLanguage || 'en');
      if (!roomName) setRoomName(ticket.title);
    }
  }, [ticket, roomName]);

  React.useEffect(() => {
    if (id && user && isCustomerSupport(user.role)) {
      markTicketNotificationsRead.mutate(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when ticket id is known
  }, [id, user?.role]);

  if (isLoading || !ticket) {
    return (
      <div className="etr-page flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const status = normalizeStatus(ticket.status);
  const canSend = isAdminOrSupport(user?.role) && (status === 'completed' || status === 'sent');
  const canCancel = isAdminOrSupport(user?.role) && status !== 'cancelled' && status !== 'sent';
  const canRelease = ticket.assignedTo?.id === user?.id && status === 'accepted';
  const canEdit = isAdminOrSupport(user?.role) && canEditTicket(ticket.status);
  const canManageLink =
    isAdminOrSupport(user?.role) &&
    (status === 'completed' || status === 'sent') &&
    !!ticket.finalVideoFilename;

  const phoneHint = formatPhoneHint(phone);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !phone.trim()) return;
    await sendMutation.mutateAsync({ id, phone: phone.trim(), language, roomName: roomName.trim() || undefined });
  };

  return (
    <div className="etr-page">
      <Link to={isAdminOrSupport(user?.role) ? '/tickets' : '/my-tickets'} className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4">
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back
      </Link>

      <div className="etr-page-header mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="etr-page-title">{ticket.title}</h1>
          {ticket.ticketNumber && <span className="text-sm font-mono text-gray-500">{ticket.ticketNumber}</span>}
          <TicketStatusBadge status={ticket.status} />
        </div>
        <p className="etr-page-subtitle mt-1">
          Slot: {formatSlot(ticket.slotStartAt)} · Branch #{ticket.branchId} · Room #{ticket.roomId}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="etr-surface p-6 space-y-3 text-sm">
            <p><strong>Requested by:</strong> {userDisplayName(ticket.requestedBy)}</p>
            <p><strong>Assigned to:</strong> {ticket.assignedTo ? userDisplayName(ticket.assignedTo) : '—'}</p>
            {ticket.customerName && <p><strong>Customer:</strong> {ticket.customerName}</p>}
            {ticket.customerPhone && <p><strong>Phone:</strong> {ticket.customerPhone}</p>}
            {ticket.notes && <p><strong>Notes:</strong> {ticket.notes}</p>}
            {ticket.processingError && <p className="text-red-600"><strong>Error:</strong> {ticket.processingError}</p>}
            {ticket.finalVideoFilename && <p><strong>Video file:</strong> {ticket.finalVideoFilename}</p>}
            {ticket.sessionId && (
              <Link to={`/sessions/${ticket.sessionId}`} className="inline-flex items-center text-primary-700">
                <Video className="h-4 w-4 mr-1" />
                View linked session
              </Link>
            )}
          </div>

          <div className="etr-surface p-6">
            <h2 className="font-semibold text-gray-900 mb-3">Timeline</h2>
            {events.length === 0 ? (
              <p className="text-sm text-gray-500">No events yet.</p>
            ) : (
              <ul className="space-y-2">
                {events.map((ev) => (
                  <li key={ev.id} className="text-sm border-l-2 border-gray-200 pl-3">
                    <span className="font-medium">{ev.eventType}</span>
                    <span className="text-gray-500 ml-2">{new Date(ev.createdAtUtc).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {canEdit && (
            <Button variant="outline" leftIcon={<Pencil className="h-4 w-4" />} onClick={() => setShowEditModal(true)}>
              Edit ticket
            </Button>
          )}

          {(shortUrl || deliveryLinks.length > 0) && (
            <div className="etr-surface p-6 space-y-3">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                Delivery link
              </h2>
              {shortUrl && (
                <a href={shortUrl} target="_blank" rel="noreferrer" className="text-sm text-primary-700 break-all block">
                  {shortUrl}
                </a>
              )}
              {deliveryLinks.length > 0 && (
                <ul className="text-xs text-gray-500 space-y-1">
                  {deliveryLinks.slice(0, 5).map((l) => (
                    <li key={l.id}>
                      {l.isActive ? 'Active' : l.revokedAtUtc ? 'Revoked' : 'Expired'} — {l.code}
                      {l.isActive && (
                        <span className="text-gray-400"> · expires {new Date(l.expiresAtUtc).toLocaleString()}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {canManageLink && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {hasActiveLink && (
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<ShieldOff className="h-4 w-4" />}
                      isLoading={revokeLinkMutation.isLoading}
                      onClick={() => {
                        if (window.confirm('Revoke the active delivery link? Customers will no longer be able to use it.')) {
                          revokeLinkMutation.mutate(ticket.id);
                        }
                      }}
                    >
                      Revoke link
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<RefreshCw className="h-4 w-4" />}
                    isLoading={regenerateLinkMutation.isLoading}
                    onClick={() => {
                      if (
                        window.confirm(
                          hasActiveLink
                            ? 'Revoke the current link and create a new one?'
                            : 'Create a new delivery link for this video?'
                        )
                      ) {
                        regenerateLinkMutation.mutate(ticket.id);
                      }
                    }}
                  >
                    {hasActiveLink ? 'Regenerate link' : 'Create link'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {canSend && (
            <form onSubmit={handleSend} className="etr-surface p-6 space-y-4">
              <h2 className="font-semibold text-gray-900">Send via WhatsApp</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp phone</label>
                <input
                  className="etr-input w-full"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+966501234567 or 05xxxxxxxx"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  International format (+966… or +962…). Local Saudi numbers (05…) are converted automatically.
                </p>
                {phoneHint ? <p className="mt-1 text-xs text-amber-700">{phoneHint}</p> : null}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                <select className="etr-input w-full" value={language} onChange={(e) => setLanguage(e.target.value)}>
                  <option value="en">English</option>
                  <option value="ar">Arabic</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Room name (template)</label>
                <input className="etr-input w-full" value={roomName} onChange={(e) => setRoomName(e.target.value)} />
              </div>
              <Button type="submit" variant="primary" leftIcon={<Send className="h-4 w-4" />} isLoading={sendMutation.isLoading}>
                Send video link
              </Button>
            </form>
          )}

          {deliveryLogs.length > 0 && (
            <div className="etr-surface p-6">
              <h2 className="font-semibold text-gray-900 mb-3">Delivery log</h2>
              <ul className="space-y-2 text-sm">
                {deliveryLogs.map((log) => (
                  <li key={log.id} className={log.success ? 'text-green-800' : 'text-red-700'}>
                    {new Date(log.createdAtUtc).toLocaleString()} — {log.phoneE164}
                    {log.success ? ' ✓' : ` — ${log.errorDetail || 'failed'}`}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col gap-2">
            {canRelease && (
              <Button variant="outline" onClick={() => releaseMutation.mutate(ticket.id)}>
                Release ticket
              </Button>
            )}
            {canCancel && (
              <Button
                variant="outline"
                leftIcon={<Ban className="h-4 w-4" />}
                onClick={() => {
                  if (window.confirm('Cancel this ticket?')) cancelMutation.mutate(ticket.id);
                }}
              >
                Cancel ticket
              </Button>
            )}
            {isAdmin(user?.role) && (
              <Button variant="danger" onClick={() => window.alert('Use Tickets list to delete.')}>
                Delete (admin)
              </Button>
            )}
          </div>
        </div>
      </div>

      <EditTicketModal open={showEditModal} ticket={ticket} onClose={() => setShowEditModal(false)} />
    </div>
  );
};

export default TicketDetail;
